import { createMemo, type Accessor } from 'solid-js';
import { formatCamelOrPascalToTitleCaseWords } from '@trimble/utilities';
import type { FormattingSettings } from '@trimble/types';
import type { ObjectPropertiesPanelCapability } from '@trimble/web3d-capabilities';
import {
  buildDisplayCommonSets,
  buildDisplayEntitySets,
  type BuildDisplaySetsContext,
} from './build-display-sets';
import { buildSharedCustomSets } from './build-shared-custom-sets';
import {
  comparePropertyNames,
  normalizePanelData,
  type NormalizedPanelModel,
} from './object-properties-panel-helper';
import type {
  CustomEntityPropertySetsInput,
  ObjectPropertiesSourceUnits,
} from './object-properties.types';

// Synthetic entity id under which custom sets shared across the whole
// multi-selection are grouped for display.
const SHARED_CUSTOM_ENTITY_ID = 'shared-custom';

export interface UseObjectPropertiesPanelModelOptions {
  capability: Accessor<ObjectPropertiesPanelCapability>;
  formattingSettings: Accessor<FormattingSettings>;
  sourceUnits: Accessor<ObjectPropertiesSourceUnits | undefined>;
  sortAlphabetically: Accessor<boolean | undefined>;
  refineNames: Accessor<boolean | undefined>;
  customEntityPropertySets: Accessor<
    readonly CustomEntityPropertySetsInput[] | undefined
  >;
  groupedFavoriteKeys: Accessor<ReadonlySet<string>>;
}

export interface UseObjectPropertiesPanelModelResult {
  data: Accessor<ReturnType<ObjectPropertiesPanelCapability['data']>>;
  selectionCount: Accessor<number>;
  isMultiSelection: Accessor<boolean>;
  panelData: Accessor<NormalizedPanelModel>;
  hasSharedProps: Accessor<boolean>;
}

/**
 * @intent Assembles the display model for the object properties panel:
 * formatting context, per-entity/common sets, multi-selection shared custom
 * sets, and normalized favorite/regular sections.
 */
export function useObjectPropertiesPanelModel(
  options: UseObjectPropertiesPanelModelOptions
): UseObjectPropertiesPanelModelResult {
  const data = createMemo(() => options.capability().data());

  const selectionCount = createMemo(
    (): number => data()?.entities?.length ?? 0
  );
  const isMultiSelection = createMemo((): boolean => selectionCount() > 1);

  const commonSets = createMemo(() => data()?.commonModelPropSets ?? []);
  const entityProps = createMemo(() => data()?.entityProps ?? []);
  const customEntityPropertySets = createMemo(
    () => options.customEntityPropertySets() ?? []
  );

  const displaySetsContext = createMemo((): BuildDisplaySetsContext => {
    const refineNames = options.refineNames() ?? false;
    const sortAlphabetically = options.sortAlphabetically() ?? false;

    return {
      formattingContext: {
        settings: options.formattingSettings(),
        sourceUnits: options.sourceUnits(),
      },
      formatName: (name) =>
        refineNames ? formatCamelOrPascalToTitleCaseWords(name) : name,
      sortPropertiesByName: (properties) =>
        sortAlphabetically
          ? [...properties].sort((a, b) => comparePropertyNames(a.name, b.name))
          : [...properties],
    };
  });

  const displayCommonSets = createMemo(() =>
    buildDisplayCommonSets(commonSets(), displaySetsContext())
  );

  const displayEntitySets = createMemo(() => {
    if (isMultiSelection()) {
      const sharedCustomSets = buildSharedCustomSets(
        data()?.entities ?? [],
        customEntityPropertySets(),
        displaySetsContext()
      );
      // Providers omit per-entity model props in multi-selection; only call
      // buildDisplayEntitySets when tests or callers still supply them.
      const modelEntitySets =
        entityProps().length > 0
          ? buildDisplayEntitySets(entityProps(), [], displaySetsContext())
          : [];
      return sharedCustomSets.length > 0
        ? [
            ...modelEntitySets,
            { entityId: SHARED_CUSTOM_ENTITY_ID, psets: sharedCustomSets },
          ]
        : modelEntitySets;
    }

    return buildDisplayEntitySets(
      entityProps(),
      customEntityPropertySets(),
      displaySetsContext()
    );
  });

  const panelData = createMemo(() =>
    normalizePanelData(
      displayCommonSets(),
      displayEntitySets(),
      options.groupedFavoriteKeys(),
      options.sortAlphabetically() ?? false
    )
  );

  const hasSharedProps = createMemo(
    (): boolean =>
      panelData().regularSets.length > 0 || panelData().favoriteSets.length > 0
  );

  return {
    data,
    selectionCount,
    isMultiSelection,
    panelData,
    hasSharedProps,
  };
}
